import { apiService } from "@/services/api";
import { getErrorMessage, isSuccessStatus } from "@/services/utils/httpUtils";
import db from "@/services/offlineDb";
import { enqueueOp } from "@/services/offlineQueue";

/**
 * Mapeo de resourceName de CRUD a tabla Dexie para offline fallback.
 * Solo las tablas offline-habilitadas están mapeadas.
 */
const DEXIE_TABLE_MAP = {
  grilla_vaca: "animales",
  lotes_vacas: "lotes",
  siembras: "siembras",
  cosechas: "cosechas",
  picados: "picados",
  inventario_veterinario: "inventario_veterinario",
  tareas: "tareas",
};

export function createCrudStore(resourceName) {
  const stateName = resourceName;

  return {
    namespaced: true,

    state: {
      [stateName]: [],
      loading: false,
      error: null,
    },

    getters: {
      [stateName](state) {
        return state[stateName];
      },

      loading(state) {
        return state.loading;
      },

      error(state) {
        return state.error;
      },

      getById(state) {
        return (id) => state[stateName].find((item) => item.id === id);
      },
    },

    mutations: {
      setState(state, items) {
        state[stateName] = items;
      },

      addItem(state, item) {
        state[stateName].push(item);
      },

      updateItem(state, updatedItem) {
        const index = state[stateName].findIndex(
          (item) => item.id === updatedItem.id
        );
        if (index !== -1) {
          state[stateName].splice(index, 1, updatedItem);
        }
      },

      removeItem(state, id) {
        state[stateName] = state[stateName].filter((item) => item.id !== id);
      },

      setLoading(state, value) {
        state.loading = value;
      },
      setError(state, error) {
        state.error = error;
      },
      clearError(state) {
        state.error = null;
      },
    },

    actions: {
      async getAllStates({ commit, rootState }) {
        commit("setLoading", true);
        commit("clearError");
        try {
          const response = await apiService.getAll(resourceName);
          if (isSuccessStatus(response.status)) {
            commit("setState", response.data);
            // console.log(
            //   "Datos obtenidos de: ",
            //   resourceName,
            //   "Datos: ",
            //   response.data
            // );
            // Guardar en Dexie para offline fallback
            const dexieTable = DEXIE_TABLE_MAP[resourceName];
            if (
              dexieTable &&
              db[dexieTable] &&
              Array.isArray(response.data) &&
              response.data.length > 0
            ) {
              let itemsToSave = response.data;
              if (dexieTable === "lotes") {
                itemsToSave = response.data.map((item) => ({
                  ...item,
                  id: item.lote_id || item.id,
                  nombre: item.nombre_lote || item.nombre,
                }));
              }
              db[dexieTable].bulkPut(itemsToSave).catch(() => {});
            }
            return response.data;
          }
        } catch (error) {
          // Si es un error 403, puede ser por falta de permisos o rol pendiente
          if (error.response?.status === 403) {
            const isPendiente =
              rootState?.userTamboStore?.currentRole === "pendiente";
            const currentRole = rootState?.userTamboStore?.currentRole;

            if (isPendiente) {
              console.warn(
                `⚠️ [${resourceName}] Usuario con rol pendiente, no puede acceder a los datos`
              );
              commit("setError", null); // No mostrar error si es pendiente
              commit("setState", []); // Limpiar datos
              return []; // ✅ Retornar vacío sin lanzar error
            } else {
              console.warn(
                `⚠️ [${resourceName}] Error 403: Sin permisos para acceder a este recurso. Rol actual: ${currentRole}`
              );
              const errorMessage =
                "No tienes permisos para acceder a esta sección. Contacta al administrador del tambo.";
              commit("setError", errorMessage);
              commit("setState", []); // Limpiar datos
              return []; // ✅ Retornar vacío sin lanzar error
            }
          } else {
            // Si estamos offline, intentar leer de Dexie primero
            const dexieTable = DEXIE_TABLE_MAP[resourceName];
            if (!navigator.onLine) {
              if (dexieTable && db[dexieTable]) {
                console.log(
                  `📴 Offline fallback: leyendo ${dexieTable} de Dexie`
                );
                let local = await db[dexieTable].toArray();
                if (dexieTable === "lotes") {
                  local = local.map((l) => ({
                    ...l,
                    lote_id: l.lote_id || l.id,
                    nombre_lote: l.nombre_lote || l.nombre,
                  }));
                }
                commit("setState", local);
                return local;
              }
              // Sin tabla Dexie → retornar vacío silenciosamente (no tirar error)
              console.log(
                `📴 Offline: ${resourceName} sin cache local, cargando vacío`
              );
              commit("setState", []);
              return [];
            }
            const errorMessage = getErrorMessage(
              error,
              `Error obteniendo ${resourceName}`
            );
            commit("setError", errorMessage);
            throw error; // Solo lanzar si NO es 403
          }
        } finally {
          commit("setLoading", false);
        }
      },

      async getStateById({ commit }, id) {
        commit("setLoading", true);
        commit("clearError");
        try {
          const response = await apiService.getById(resourceName, id);
          if (isSuccessStatus(response.status)) {
            return response.data;
          }
        } catch (error) {
          const dexieTable = DEXIE_TABLE_MAP[resourceName];
          if (!navigator.onLine && dexieTable && db[dexieTable]) {
            const numId = parseInt(id, 10);
            const local =
              (await db[dexieTable].where("id").equals(numId).first()) ||
              (await db[dexieTable].where("id").equals(id).first()) ||
              (await db[dexieTable].where("localId").equals(numId).first());
            if (local) {
              return local;
            }
          }
          const errorMessage = getErrorMessage(
            error,
            `Error obteniendo ${resourceName}`
          );
          commit("setError", errorMessage);
          throw error;
        } finally {
          commit("setLoading", false);
        }
      },

      async create({ commit }, payload) {
        commit("setLoading", true);
        commit("clearError");
        const dexieTable = DEXIE_TABLE_MAP[resourceName];
        if (!navigator.onLine && dexieTable && db[dexieTable]) {
          try {
            await enqueueOp(
              dexieTable,
              "create",
              payload,
              `/${resourceName}`,
              "POST"
            );
            const tempId = Date.now();
            const localItem = {
              ...payload,
              id: tempId,
              lote_id: payload.lote_id || tempId,
              nombre_lote: payload.nombre_lote || payload.nombre,
              nombre: payload.nombre_lote || payload.nombre,
              _offline: true,
            };
            await db[dexieTable].put(localItem).catch(() => {});
            commit("addItem", localItem);
            return { status: 201, data: localItem };
          } finally {
            commit("setLoading", false);
          }
        }
        try {
          const response = await apiService.create(resourceName, payload);
          if (isSuccessStatus(response.status)) {
            commit("addItem", response.data);
            return response;
          }
        } catch (error) {
          const errorMessage = getErrorMessage(
            error,
            `Error creando ${resourceName}`
          );
          commit("setError", errorMessage);
          throw error;
        } finally {
          commit("setLoading", false);
        }
      },

      async update({ commit }, payload) {
        commit("setLoading", true);
        commit("clearError");
        const dexieTable = DEXIE_TABLE_MAP[resourceName];
        const itemId = payload.id || payload.lote_id;
        if (!navigator.onLine && dexieTable && db[dexieTable]) {
          try {
            await enqueueOp(
              dexieTable,
              "update",
              payload,
              `/${resourceName}/${itemId}`,
              "PATCH"
            );
            const numId = parseInt(itemId, 10);
            const existing =
              (await db[dexieTable].where("id").equals(numId).first()) ||
              (await db[dexieTable].where("id").equals(itemId).first()) ||
              (await db[dexieTable].where("localId").equals(numId).first());
            const updated = { ...(existing || {}), ...payload };
            if (existing?.localId) {
              await db[dexieTable]
                .update(existing.localId, payload)
                .catch(() => {});
            } else {
              await db[dexieTable].put(updated).catch(() => {});
            }
            commit("updateItem", updated);
            return { status: 200, data: updated };
          } finally {
            commit("setLoading", false);
          }
        }
        try {
          const response = await apiService.update(
            resourceName,
            payload.id,
            payload
          );
          if (isSuccessStatus(response.status)) {
            commit("updateItem", response.data);
            return response;
          }
        } catch (error) {
          const errorMessage = getErrorMessage(
            error,
            `Error actualizando ${resourceName}`
          );
          commit("setError", errorMessage);
          throw error;
        } finally {
          commit("setLoading", false);
        }
      },

      async delete({ commit }, id) {
        commit("setLoading", true);
        commit("clearError");
        const dexieTable = DEXIE_TABLE_MAP[resourceName];
        if (!navigator.onLine && dexieTable && db[dexieTable]) {
          try {
            await enqueueOp(
              dexieTable,
              "delete",
              {},
              `/${resourceName}/${id}`,
              "DELETE"
            );
            const numId = parseInt(id, 10);
            const existing =
              (await db[dexieTable].where("id").equals(numId).first()) ||
              (await db[dexieTable].where("id").equals(id).first()) ||
              (await db[dexieTable].where("localId").equals(numId).first());
            if (existing?.localId) {
              await db[dexieTable].delete(existing.localId).catch(() => {});
            }
            commit("removeItem", id);
            return { status: 200 };
          } finally {
            commit("setLoading", false);
          }
        }
        try {
          const response = await apiService.delete(resourceName, id);
          if (isSuccessStatus(response.status)) {
            commit("removeItem", id);
            return response;
          }
        } catch (error) {
          const errorMessage = getErrorMessage(
            error,
            `Error eliminando ${resourceName}`
          );
          commit("setError", errorMessage);
          throw error;
        } finally {
          commit("setLoading", false);
        }
      },

      async executeApiCall(
        { commit },
        { apiCallFunction, onSuccessCallback, errorMessage }
      ) {
        commit("setLoading", true);

        commit("clearError");

        try {
          const apiResponse = await apiCallFunction();

          if (isSuccessStatus(apiResponse.status)) {
            if (onSuccessCallback) {
              onSuccessCallback(commit, apiResponse.data);
            }

            return apiResponse;
          }
        } catch (error) {
          const errorMsg = getErrorMessage(error, errorMessage);

          commit("setError", errorMsg);

          throw error;
        } finally {
          commit("setLoading", false);
        }
      },
    },
  };
}
